import { Tag } from 'antd';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import { AppTableControls } from 'common/TableControls/TableControls';
import { educationParallelTextRepresentation } from 'entities/education-class';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { formatCountWithDeclension } from 'shared/helpers/utils/get-declension';
import { AppText } from 'shared/UI/UIKit/Text';
import type { AppTableColumn } from 'shared/UI/UIKit/Table';
import type { EducationClass, Parallel } from 'services/api';
import type { TeacherWorkload } from 'shared/api';
import type { TeacherWorkloadsTableRow } from '../model/types';
import styles from '../ui/styles.module.scss';

interface GetColumnsProps {
  onEdit: (record: TeacherWorkload) => void;
  onDelete: (record: TeacherWorkload) => void;
  onCopy: (record: TeacherWorkload) => void;
  educationClasses: EducationClass[];
}

export const getColumns = ({
  onEdit,
  onDelete,
  onCopy,
  educationClasses,
}: GetColumnsProps): AppTableColumn<TeacherWorkloadsTableRow>[] => [
  {
    key: 'code',
    title: 'Код преподавателя',
    dataIndex: ['code'],
    minWidth: 200,
    ellipsis: true,
    className: styles.codeColumn,
    onCell: (record: TeacherWorkloadsTableRow) => ({
      rowSpan: record.qualificationIndex === 0 ? record.qualificationsCount : 0,
    }),
    render: (value: string, record: TeacherWorkloadsTableRow) => {
      if (record.qualificationIndex !== 0) {
        return null;
      }

      const subjectsCount = record.teacherWorkload.qualifications?.length ?? 0;
      const subjectsLabel = formatCountWithDeclension(subjectsCount, [
        'предмет',
        'предмета',
        'предметов',
      ]);

      return (
        <AppFlex alignItems="flex-start" justifyContent="flex-start" unit="4">
          <AppText>{value}</AppText>
          <AppText type="secondary">{subjectsLabel}</AppText>
        </AppFlex>
      );
    },
  },
  {
    key: 'rooms',
    title: 'Аудитория преподавателя',
    dataIndex: ['rooms'],
    minWidth: 200,
    sorter: false,
    ellipsis: true,
    className: styles.preferredRoomColumn,
    onCell: (record: TeacherWorkloadsTableRow) => ({
      rowSpan: record.qualificationIndex === 0 ? record.qualificationsCount : 0,
    }),
    render: (rooms: TeacherWorkloadsTableRow['rooms']) => {
      if (!rooms?.length) {
        return null;
      }
      return (
        <AppFlex dir="column">
          {rooms.map((room) => (
            <span key={room.id}>
              {room.number} - {room.name}
            </span>
          ))}
        </AppFlex>
      );
    },
  },
  {
    key: 'subjects',
    title: 'Предметы',
    dataIndex: ['qualification'],
    minWidth: 300,
    sorter: false,
    className: styles.subjectsColumn,
    render: (qualification: TeacherWorkloadsTableRow['qualification']) =>
      qualification ? (
        <div style={{ lineHeight: '22px' }}>
          {formatSubjectWithLevel(qualification.subjectName, qualification.educationLevel)}
        </div>
      ) : null,
  },
  {
    key: 'educationalClasses',
    title: 'Классы',
    dataIndex: ['qualification'],
    minWidth: 300,
    sorter: false,
    render: (qualification: TeacherWorkloadsTableRow['qualification']) => {
      if (!qualification) {
        return null;
      }

      const formattedClasses = qualification.educationalClassIds
        .map((id) => {
          const ec = educationClasses.find((c) => c.id === id);
          if (!ec) {
            return '';
          }

          const parallelText = educationParallelTextRepresentation[ec.parallel as Parallel];
          return `${parallelText}${ec.litera}`;
        })
        .filter(Boolean);

      return (
        <div style={{ lineHeight: '22px' }}>
          {formattedClasses.map((c) => (
            <Tag className={styles.classTag} key={c}>
              {c}
            </Tag>
          ))}
        </div>
      );
    },
  },
  {
    key: 'controls',
    title: '',
    dataIndex: [],
    maxWidth: '10rem',
    fixed: 'right',
    onCell: (record: TeacherWorkloadsTableRow) => ({
      rowSpan: record.qualificationIndex === 0 ? record.qualificationsCount : 0,
    }),
    render: (_: any, record: TeacherWorkloadsTableRow) => (
      <AppTableControls
        edit={() => onEdit(record.teacherWorkload)}
        remove={() => onDelete(record.teacherWorkload)}
        copy={() => onCopy(record.teacherWorkload)}
      />
    ),
  },
];
